import React, {
  useState,
  Suspense,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { makeId, searchParentRef } from 'util/helpers';
import { v4 as uuidv4 } from 'uuid';
import FirstPersonDragControls from './FirstPersonDragControls';
import { Select, PerspectiveCamera } from '@react-three/drei';
import { Perf } from 'r3f-perf';
import {
  EDITOR_MODE,
  SceneEditor,
} from '@src/components/scene/react-three-components/sceneEditor';
import EditorComposer from './components/ui/EditorComposer';
import EditorSwitcher from '@src/components/side-bar-menu/timeline-node-manager/ui/EditorSwitcher';
import TimelineNodeManager from '@src/components/side-bar-menu/timeline-node-manager/TimelineNodeManager';
import {
  useCreateUserSceneAssetsMutation,
  useDeleteUserSceneAssetsMutation,
  useDeleteLightPropsMutation,
  useUpdateUserSceneAssetsMutation,
  useUpdateUserSceneMutation,
  useSceneTemplatesQuery,
  useFindManyUserSceneAssetsQuery,
  useCreateUserSceneMutation,
  useUserScenesQuery,
  useFindFirstLightPropsLazyQuery,
  FindManyUserSceneAssetsDocument,
  useCameraConfigLazyQuery,
  useCreateOneCameraConfigMutation,
  useUpdateOneCameraConfigMutation,
  useGetUnityPackageMutation,
  useSetIsCameraThirdPersonSceneMutation,
  useUpdateSceneAssetsMutation,
  useFindFirstPostProcessingSettingsQuery,
  useFindFirstGraphicsSettingsLazyQuery,
  useGetSceneTimelineByIdQuery,
} from '@src/components/generated/graphql';
import { useDeleteAiAvatarMutation } from '@src/components/generated/graphql';
import styles from './editor.module.css';
import {
  Asset,
  SceneAsset,
} from '@src/components/scene/react-three-components/sceneAsset';
import { useRouter } from 'next/router';
import * as THREE from 'three';
import saveScene from './components/lib/save-scene';
import { Canvas } from '@react-three/fiber';
import useCameraControls from './components/ui/useCameraControls';
import { Skybox } from '@src/components/scene/react-three-components/skybox';
import { ModelErrorHandler } from '@src/components/error-handling/modelErrorHandler';
import { Renderer } from '@src/components/scene/react-three-components/renderer';
import { EventManager } from '@src/components/scene/react-three-components/eventManager';
import PostprocessingSettings from '@src/components/scene/react-three-components/postprocessingSettings';
import { ASSET_TYPE } from '@src/components/scene/react-three-components/sceneAsset';
import { DEFAULT_SCENE_TEMPLATE } from 'pages/new-template';
import EditorDrawer from '@src/components/scene/canvas-html/editorDrawer/editorDrawer';
import LightSettingsDrawer from '@src/components/scene/canvas-html/editorDrawer/objectTab/objectLightProps';
import MaterialEditorDrawer from '@src/components/scene/canvas-html/editorDrawer/objectTab/materialEditor/materialEditor';
import ShaderPropertiesDrawer from './components/ui/ShaderPropertiesDrawer';
import exportScene from './components/lib/export-scene';
import { openNotification } from 'util/helpers';
import { EVENTS } from 'util/constants';
import { getObjectId } from 'util/helpers';
import EditorMenu from './components/ui/EditorMenu';
import useUndoRedo from '@src/components/hooks/useUndoRedo';
import { assetChanged } from './components/lib/asset-changed';
import { Splitter } from 'antd';
import { useSceneLoaded } from '@src/components/hooks/useSceneLoaded';
import { EnvMapType } from '@src/components/generated/graphql';

const cameraInitialPosition: THREE.Vector3 = new THREE.Vector3(0, 50, 50);
const DPR = 1.0;
export const DEFAULT_LIGHT_TEMPLATE = 0; // disabled;

const Editor = ({
  skyBoxIn,
  buildScene,
  lightsIn,
  email,
  user,
  isSaveScene,
  setIsSaveScene,
  isExportScene,
  setIsExportScene,
  setExportSceneProgress,
  tourRefs,
  isSignIn = true,
  fromTemplate = false,
  isLoginOpen = false,
  setIsLoginOpen = () => {},
  setIsTourOpen = () => {},
}: any) => {
  const [createAsset] = useCreateUserSceneAssetsMutation();
  const [createScene] = useCreateUserSceneMutation();
  const [updateAsset] = useUpdateUserSceneAssetsMutation();
  const [updateScene] = useUpdateUserSceneMutation();
  const [findFirstLightProps] = useFindFirstLightPropsLazyQuery();
  const [deleteAIAvatarMutation] = useDeleteAiAvatarMutation();
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [forceRender, setForceRender] = useState(false);
  const { sceneFirstFullyLoaded } = useSceneLoaded();
  const cancelExportRef = useRef(false);
  const setCancelExportRef = (value: boolean) => {
    cancelExportRef.current = value;
  };
  const [deleteAssetScene] = useDeleteUserSceneAssetsMutation();
  const [deleteLightProps] = useDeleteLightPropsMutation();
  const [getCameraConfig] = useCameraConfigLazyQuery();
  const [createCameraConfig] = useCreateOneCameraConfigMutation();
  const [updateCameraConfig] = useUpdateOneCameraConfigMutation();
  const [getUnityPackage] = useGetUnityPackageMutation();
  const [updateCameraType] = useSetIsCameraThirdPersonSceneMutation();
  const [updateManyAssets] = useUpdateSceneAssetsMutation();
  const [currentCameraConfig, setCurrentCameraConfig] = useState<any>({
    id: undefined,
    far: 2500,
    near: 0.15,
    fov: 75,
    maxSpeed: 32,
    minSpeed: 15,
    isThirdPerson: false,
  });
  const router = useRouter();
  const [selectedModelProps, setSelectedModelProps] = useState<any>();
  const [isEnableShadowLight, setIsEnableShadowLight] = useState<boolean>(true);
  const [editorMode, setEditorMode] = useState<EDITOR_MODE>(
    EDITOR_MODE.TRANSLATE
  );
  const [isEditorDrawerOpen, setIsEditorDrawerOpen] = useState<boolean>(false);
  const [isLightDrawerOpen, setIsLightDrawerOpen] = useState<boolean>(false);
  const [isShaderDrawerOpen, setIsShaderDrawerOpen] = useState<boolean>(false);
  const [isMaterialDrawerOpen, setIsMaterialDrawerOpen] =
    useState<boolean>(false);
  const [isOpenWorlds, setIsOpenWorlds] = useState<boolean>(false);
  const [camera, setCamera] = useState<any>(null);
  const cameraControlsRef = useRef<any>(null);
  const [selectedSkybox, setSelectedSkybox] = useState<any>();
  const [lightTemplate, setLightTemplate] = useState(DEFAULT_LIGHT_TEMPLATE);
  const [selectedSceneTemplate, setSelectedSceneTemplate] = useState<any>();
  const [selectedSceneThumbnail, setSelectedThumbnail] = useState<string>('');
  const [isTransformControlsActive, setIsTransformControlsActive] =
    useState<boolean>(false);
  const [selectedModel, setSelectedModel] =
    useState<React.JSX.Element | null>();
  const [sceneAssets, setSceneAssets] = useState<React.JSX.Element[]>([]);
  const [assetsToDelete, setAssetsToDelete] = useState(new Set<any>());
  const [isAddedInitialPoint, setIsAddedInitialPoint] =
    useState<boolean>(false);
  const [scene, setScene] = useState<any>(null);
  const [postprocessingSettingsId, setPostprocessingSettingsId] = useState<
    string | null
  >(null);
  const [findFirstGraphicsSettings] = useFindFirstGraphicsSettingsLazyQuery();
  const [isTimelineMode, setIsTimelineMode] = useState<boolean>(false);
  const [isTimelineOpen, setIsTimelineOpen] = useState<boolean>(false);
  const [isComposerCollapsed, setIsComposerCollapsed] = useState<boolean>(true);
  const [timelineSetNodes, setTimelineSetNodes] = useState<any>(null);
  const [splitterKey, setSplitterKey] = useState<number>(0);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [initialAssetsState, setInitialAssetsState] = useState<string>('');
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [isPOIModalOpen, setIsPOIModalOpen] = useState(false);
  const [isPortalModalOpen, setIsPortalModalOpen] = useState(false);
  const [showPerf, setShowPerf] = useState<boolean>(false);

  const saveTimelineRef = useRef<VoidFunction | null>(null);

  const handleSetTimelineSetNodes = useCallback((newSetNodes: any) => {
    setTimelineSetNodes(newSetNodes);
  }, []);

  const sceneId = Array.isArray(router.query?.id)
    ? router.query.id[0]
    : router.query?.id;

  const { refetch: refetchTimelineData } = useGetSceneTimelineByIdQuery({
    variables: {
      where: {
        sceneId: sceneId as string,
      },
    },
    skip: !sceneId,
  });

  useCameraControls({ camera, currentCameraConfig });

  const fetchGraphicsSettings = async (graphicsSettingsId: string) => {
    try {
      const result = await findFirstGraphicsSettings({
        variables: {
          where: {
            id: {
              equals: graphicsSettingsId,
            },
          },
        },
      });

      if (result.data?.findFirstGraphicsSettings?.postProcessingSettingsId) {
        setPostprocessingSettingsId(
          result.data.findFirstGraphicsSettings.postProcessingSettingsId
        );
      }
    } catch (error) {
      console.error('Error fetching graphics settings:', error);
    }
  };

  const { data: postprocessingData } = useFindFirstPostProcessingSettingsQuery({
    variables: {
      where: {
        id: {
          equals: postprocessingSettingsId,
        },
      },
    },
    skip: !postprocessingSettingsId,
    fetchPolicy: 'no-cache',
  });

  function checkIsAssetToDelete(id: 'string') {
    if (assetsToDelete.has(id)) {
      const newSet = new Set(assetsToDelete);
      newSet.delete(id);
      setAssetsToDelete(newSet);
    }
  }

  const {
    state: selectableAssets,
    dispatch: setSelectableAssets,
    history,
    undo,
    redo,
  } = useUndoRedo({ checkIsAssetToDelete });

  const cleanup = () => {
    selectableAssets.forEach((sa) => {
      sa.props.asset.current.traverse((o) => {
        if (o.geometry) {
          o.geometry.dispose();
        }

        if (o.material) {
          if (o.material.length) {
            for (let i = 0; i < o.material.length; ++i) {
              o.material[i].dispose();
            }
          } else {
            o.material.dispose();
          }
        }
      });
    });
  };

  const {
    data: sceneTemplates,
    loading: l,
    error: e,
  } = useSceneTemplatesQuery();

  useEffect(() => {
    return () => {
      cleanup;
    };
  }, []);

  /////////////////////////////////////////////////
  //  Need check execute this hook because when
  //  save execute the oncompleted
  ////////////////////////////////////////////////
  const {
    loading: l1,
    error: e1,
    refetch: refetchAssets,
  } = useFindManyUserSceneAssetsQuery({
    variables: {
      where: {
        userSceneId: {
          equals: sceneId,
        },
      },
    },
    skip: !sceneTemplates,
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (isFirstRender) {
        setIsFirstRender(() => false);
        loadScene(data.findManyUserSceneAssets);
      }
    },
  });

  const { loading: l2, error: e2 } = useUserScenesQuery({
    variables: {
      where: {
        id: {
          equals: sceneId,
        },
      },
    },
    skip: !sceneTemplates,
    fetchPolicy: 'no-cache',
    onCompleted: async (data) => {
      const skyboxIndex = data.userScenes[0].skybox || 7;
      const lightTemplateScene = data.userScenes[0].lightTemplate;
      const isSkyBox2025 = data.userScenes[0].isSkyBox2025 || false;
      const envMapType = data.userScenes[0].envMapType || EnvMapType.Hdri;
      const hdriType = data.userScenes[0].hdri || 'day';
      const hdriIntensity = data.userScenes[0].envIntensity || 1;
      setLightTemplate(
        typeof lightTemplateScene !== 'undefined'
          ? lightTemplateScene!
          : DEFAULT_LIGHT_TEMPLATE
      );
      setSelectedSkybox({
        ...skyBoxIn[skyboxIndex].object,
        index: skyboxIndex,
        hdriType,
        hdriIntensity,
        isSkyBox2025,
        envMapType,
      });
      const {
        lightTemplate = DEFAULT_LIGHT_TEMPLATE,
        cameraConfigId,
        isCameraThirdPerson,
        graphicsSettingsId,
      } = data.userScenes[0];

      if (graphicsSettingsId) {
        fetchGraphicsSettings(graphicsSettingsId);
      }
      if (cameraConfigId) {
        const currentCameraConfig = await getCameraConfig({
          variables: {
            where: {
              id: cameraConfigId,
            },
          },
        });
        setCurrentCameraConfig(() => {
          return {
            id: cameraConfigId,
            ...currentCameraConfig.data?.cameraConfig,
            isThirdPerson: isCameraThirdPerson,
          };
        });
      }
    },
  });

  const checkEditorState = useCallback(() => {
    return [l2, l1, l, e, e1, e2].every((e) => !new Boolean(e));
  }, [l2, l1, l, e, e1, e2]);

  const deleteSelectedModel = async () => {
    if (!selectedModel) {
      return;
    }

    const asset = selectedModel?.props?.asset;

    if (asset?.name?.includes('Initial-Point')) {
      setIsAddedInitialPoint(false);
    }
    setSelectableAssets({
      type: 'delete',
      deleteElement: { ...selectedModel },
    });

    const id = asset?.id;

    if (id) {
      if (asset?.type === ASSET_TYPE.MISC.LIGHT) {
        try {
          deleteLightProps({
            variables: {
              where: {
                id: selectedModel.props.light.id,
              },
            },
          });
        } catch (err) {
          console.error(err);
        }
      } else if (asset?.type === ASSET_TYPE.AI_AVATARS) {
        const botConfig = asset?.metadata?.botConfig;
        const botId = botConfig?.botId;
        try {
          await deleteAIAvatarMutation({
            variables: {
              where: {
                id: botId,
              },
            },
          });
        } catch (error) {
          console.log('Avatar may have already been deleted:', error);
        }
      }

      const newSet = new Set(assetsToDelete);
      newSet.add(id);
      setAssetsToDelete(newSet);
    }

    setSelectedModel(() => null);
    setIsLightDrawerOpen(false);
  };

  async function loadSceneTemplate(baseModelURL, sceneThumbnail) {
    if (!baseModelURL) {
      sceneAssets.forEach(async (sceneAsset) => {
        if (!sceneAsset.props.asset.id) return;
        await deleteAssetScene({
          variables: {
            where: {
              id: sceneAsset.props.asset.id,
            },
          },
          refetchQueries: [FindManyUserSceneAssetsDocument],
        });
      });
      setSceneAssets([]);
      setSelectedSceneTemplate('');

      return;
    }

    const assetNames = ['AssetPack', 'HighPack', 'SignPack', 'TeleportFloor'];

    const assetsURL: string[] = assetNames.map(
      (assetName) => `${baseModelURL}${assetName}.gltf`
    );

    const sceneAssetLength = sceneAssets.length;

    setSceneAssets(() =>
      assetsURL.map((url, index) => {
        const asset: Asset = {
          type: assetNames[index].toUpperCase(),
          url,
          name: assetNames[index],
          id: sceneAssetLength ? sceneAssets[index].props.asset.id : '',
          ref: React.createRef(),
          previewImage:
            sceneAssets[index]?.props?.asset?.previewImage ||
            sceneAssets[index]?.props?.asset?.url ||
            '',
        };

        const sceneAsset = (
          <SceneAsset
            key={`Scene-Asset-${index}`}
            asset={asset}
            instanceId={makeId(10)}
          />
        );

        return sceneAsset;
      })
    );
    setSelectedThumbnail(() => sceneThumbnail);
    setSelectedSceneTemplate(() => baseModelURL);
  }

  async function loadScene(assets: any) {
    if (!isSignIn || fromTemplate) {
      const template = window.localStorage.getItem('new-template');
      if (template) {
        let state;
        try {
          state = JSON.parse(template);
        } catch (err) {
          console.log(err);
          return;
        }

        loadSceneTemplate(
          state.selectedSceneTemplate,
          state.selectedSceneThumbnail
        );

        state.selectableAssets.map((el, i) => {
          const instanceId = makeId(10);
          const objectID = getObjectId(el.name, selectableAssets);
          const assetType = {
            type: ASSET_TYPE.GLTF.OTHER,
            isPortal: false,
            url: el.url,
            name: `${el.name}-${objectID}`,
            previewImage: el.previewImage || el.url || '',
            id: '',
            ref: React.createRef(),
            textureToChange: '',
            textureToChangeNormal: '',
            textureToChangeRoughness: '',
            textureToChangeMetalness: '',
          };
          const sceneAsset = (
            <SceneAsset
              key={`Scene-Asset-${instanceId}`}
              position={
                new THREE.Vector3(el.position.x, el.position.y, el.position.z)
              }
              scale={new THREE.Vector3(el.scale.x, el.scale.y, el.scale.z)}
              rotation={
                new THREE.Euler(el.rotation.x, el.rotation.y, el.rotation.z)
              }
              castShadow={el.castShadow}
              receiveShadow={el.receiveShadow}
              instanceId={instanceId}
              asset={assetType}
            />
          );

          setSelectableAssets({ type: 'add', newElement: { ...sceneAsset! } });
        });
      } else {
        const isBlankTemplate = DEFAULT_SCENE_TEMPLATE < 0;
        loadSceneTemplate(
          isBlankTemplate
            ? null
            : sceneTemplates?.sceneTemplates[DEFAULT_SCENE_TEMPLATE]
                .baseModelURL,
          isBlankTemplate
            ? ''
            : sceneTemplates?.sceneTemplates[DEFAULT_SCENE_TEMPLATE].thumbnail
        );
      }
      window.dispatchEvent(new CustomEvent(EVENTS.SCENE_LOADED));
      return;
    }

    const { sceneAssets, selectableAssets } = await buildReactSceneAssets(
      assets
    );

    const assetURL = sceneAssets[0]?.props?.asset?.url;
    if (assetURL) {
      const baseURL = assetURL.substring(0, assetURL.lastIndexOf('/') + 1);
      setSelectedSceneTemplate(baseURL);
    }

    setSceneAssets(() => sceneAssets);
    setSelectableAssets({ type: 'set', newState: selectableAssets });
    window.dispatchEvent(new CustomEvent(EVENTS.SCENE_LOADED));
  }

  async function buildReactSceneAssets(assets: any) {
    return await assets.reduce(
      async (accP: any, act: any) => {
        const acc = await accP;
        let metadata = {};

        try {
          metadata = JSON.parse(act.metadata);
        } catch (err) {
          console.error('Error parsing metadata:', err);
        }
        const asset = {
          ...act,
          metadata: metadata,
          ref: React.createRef(),
        };
        let light;
        if (act.type === ASSET_TYPE.MISC.LIGHT) {
          const lightProps = await findFirstLightProps({
            variables: {
              where: {
                id: {
                  equals: act.lightId,
                },
              },
            },
          });
          light = {
            ...lightProps.data?.findFirstLightProps,
            ref: React.createRef(),
          };
        }
        const position = new THREE.Vector3(
          act.position[0],
          act.position[1],
          act.position[2]
        );
        const rotation = new THREE.Euler(
          act.rotation[0],
          act.rotation[1],
          act.rotation[2]
        );
        const scale = new THREE.Vector3(
          act.scale[0],
          act.scale[1],
          act.scale[2]
        );

        const instanceId = makeId(10);
        if (act.type === ASSET_TYPE.INITIAL_POINT) {
          setIsAddedInitialPoint(true);
        }

        if (
          [
            ASSET_TYPE.GLTF.HIGHPACK,
            ASSET_TYPE.GLTF.TELEPORTFLOOR,
            ASSET_TYPE.GLTF.SIGNPACK,
            ASSET_TYPE.GLTF.ASSETPACK,
          ].includes(act.type)
        ) {
          const sceneAsset = (
            <SceneAsset
              key={`Scene-Asset-${acc.sceneAssets.length}`}
              position={position}
              rotation={rotation}
              scale={scale}
              asset={asset}
              instanceId={instanceId}
              castShadow={act.castShadow}
              receiveShadow={act.receiveShadow}
            />
          );
          acc.sceneAssets.push(sceneAsset);
        } else {
          //others assets
          const sceneAsset = (
            <SceneAsset
              key={`Scene-Asset-${instanceId}`}
              position={position}
              rotation={rotation}
              scale={scale}
              asset={asset}
              instanceId={instanceId}
              light={light}
              castShadow={act.castShadow}
              receiveShadow={act.receiveShadow}
            />
          );
          acc.selectableAssets.push(sceneAsset);
        }
        return acc;
      },
      {
        selectableAssets: [],
        sceneAssets: [],
      }
    );
  }

  async function refetchSelectableAssetsState() {
    const result = await refetchAssets();
    if (result.data?.findManyUserSceneAssets) {
      const { selectableAssets } = await buildReactSceneAssets(
        result.data?.findManyUserSceneAssets
      );
      setSelectableAssets({ type: 'set', newState: selectableAssets });
    }
  }

  async function handleExportScene() {
    await exportScene({
      isSignIn,
      saveNewTemplate,
      setExportSceneProgress,
      setIsExportScene,
      isExportScene,
      setCancelExportRef,
      scene,
      cancelExportRef,
      email,
      getUnityPackage,
      selectableAssets,
      sceneAssets,
    });
  }

  async function saveCameraConfig(sceneId) {
    if (!currentCameraConfig.id) {
      await createCameraConfig({
        variables: {
          data: {
            fov: currentCameraConfig.fov,
            far: currentCameraConfig.far,
            near: currentCameraConfig.near,
            maxSpeed: currentCameraConfig.maxSpeed,
            minSpeed: currentCameraConfig.minSpeed,
            UserScene: {
              connect: [
                {
                  id: sceneId,
                },
              ],
            },
          },
        },
      });
    } else {
      await updateCameraConfig({
        variables: {
          where: {
            id: currentCameraConfig.id,
          },
          data: {
            fov: { set: currentCameraConfig.fov },
            far: { set: currentCameraConfig.far },
            near: { set: currentCameraConfig.near },
            maxSpeed: { set: currentCameraConfig.maxSpeed },
            minSpeed: { set: currentCameraConfig.minSpeed },
          },
        },
      });
    }

    await updateCameraType({
      variables: {
        where: {
          id: sceneId as string,
          enabled: currentCameraConfig.isThirdPerson,
        },
      },
    });
  }

  function saveNewTemplate() {
    const studioState = {
      selectedSceneTemplate,
      selectableAssets: [] as any,
      selectedSceneThumbnail,
    };

    studioState.selectableAssets = selectableAssets.map((e) => {
      return {
        name: e.props.asset.name,
        url: e.props.asset.url,
        position: e.props.position,
        rotation: e.props.rotation,
        scale: e.props.scale,
      };
    });

    window.localStorage.setItem('new-template', JSON.stringify(studioState));
    setIsLoginOpen(true);
  }

  async function cleanUpDeletedAssets() {
    if (!assetsToDelete.size) {
      return;
    }
    const promises: Promise<Boolean>[] = [];
    for (let id of assetsToDelete) {
      const del: Promise<Boolean> = new Promise(async (res, rej) => {
        await deleteAssetScene({
          variables: {
            where: {
              id,
            },
          },
          refetchQueries: [FindManyUserSceneAssetsDocument],
        });
        res(true);
      });

      promises.push(del);
    }
    await Promise.allSettled(promises);
    setAssetsToDelete(new Set());
  }

  const handleExportCancel = () => {
    openNotification(
      'Stop',
      'Canceling export, please wait...',
      'bottomRight',
      10,
      'error'
    );
    setCancelExportRef(true);
  };

  const handleStreamNotification = (e) => {
    openNotification('Info', e.detail.message, 'top', 5, 'info');
  };

  function handleAddPositionCapturePoint(e: any) {
    const { nodeId, actionName, propertyName, propertyKey } = e.detail;

    handleEditorClick();
    const position = new THREE.Vector3(0, 2, 0);
    const rotation = new THREE.Euler(0, 0, 0);
    const instanceId = uuidv4();

    const sceneAsset = (
      <SceneAsset
        key={`Action-Capture-Point-${instanceId}`}
        position={position}
        scale={new THREE.Vector3(1, 1, 1)}
        rotation={rotation}
        instanceId={instanceId}
        asset={{
          type: ASSET_TYPE.PRIMITIVE.POSITION_POINT,
          url: '',
          name: 'Action-Capture-Point',
          metadata: {
            nodeId,
            actionName,
            propertyName,
            propertyKey,
          },
          ref: React.createRef(),
        }}
      />
    );
    setSelectableAssets({ type: 'add', newElement: { ...sceneAsset } });
  }

  const handleSaveActionPosition = (event: Event) => {
    const customEvent = event as CustomEvent;
    const { assetToDelete, nodeId, position, propertyKey } = customEvent.detail;

    setSelectableAssets({
      type: 'delete',
      deleteElement: { ...assetToDelete },
    });

    window.dispatchEvent(
      new CustomEvent('update_action_position', {
        detail: {
          nodeId,
          position,
          propertyKey,
        },
      })
    );

    handleTimelineClick();
  };

  const handleComposerCollapsedChanged = (event: Event) => {
    const customEvent = event as CustomEvent;
    const { isCollapsed } = customEvent.detail;
    setIsComposerCollapsed(isCollapsed);
  };

  const handleSceneAssetChanged = (event: Event) => {
    const customEvent = event as CustomEvent;
    const { hasChanges } = customEvent.detail;
    setHasChanges(hasChanges);

    window.dispatchEvent(
      new CustomEvent('editor_has_changes', {
        detail: { hasChanges },
      })
    );
  };

  useEffect(() => {
    if (isSaveScene) {
      onSaveScene();
      setIsSaveScene(() => false);
    }
  }, [isSaveScene]);

  useEffect(() => {
    if (isExportScene) {
      handleExportScene();
    }
  }, [isExportScene]);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (scene && sceneFirstFullyLoaded) {
      const points: THREE.Vector3[] = [];
      points.push(new THREE.Vector3(0, 0, -50));
      points.push(new THREE.Vector3(0, 0, 50));

      const geometry = new THREE.BufferGeometry().setFromPoints(points);

      const material = new THREE.LineBasicMaterial({
        color: 0xec2d30,
      });

      const line = new THREE.Line(geometry, material);
      scene.add(line);

      timer = setTimeout(() => {
        setForceRender((prev) => !prev);

        if (scene.renderer) {
          scene.renderer.shadowMap.enabled = true;
          scene.renderer.shadowMap.needsUpdate = true;
        }
      }, 2000);
    }
    return () => {
      timer && clearTimeout(timer);
    };
  }, [scene, sceneFirstFullyLoaded]);

  useEffect(() => {
    const lastAction = history?.[0];
    if (lastAction && lastAction.type === 'add') {
      handleAutoSelectNewAsset(lastAction.newElement);
    }
  }, [selectableAssets, history]);

  const handleAutoSelectNewAsset = (element: React.JSX.Element) => {
    const asset = element?.props?.asset;
    if (asset) {
      const attemptSelect = (retries = 0, maxRetries = 100) => {
        if (asset.ref.current) {
          selectObject(asset.ref.current);
        } else if (retries < maxRetries) {
          setTimeout(() => attemptSelect(retries + 1, maxRetries), 100);
        } else {
          console.warn('Failed to auto-select asset');
        }
      };

      attemptSelect();
    }
  };

  useEffect(() => {
    document.addEventListener('cancel_export', handleExportCancel);
    window.addEventListener(
      'refetch_selectable_assets',
      refetchSelectableAssetsState
    );
    window.addEventListener(
      'stream_editor_notification',
      handleStreamNotification
    );
    window.addEventListener(
      'add_position_capture_point',
      handleAddPositionCapturePoint
    );
    window.addEventListener('save_action_position', handleSaveActionPosition);
    window.addEventListener(
      'composer_collapsed_changed',
      handleComposerCollapsedChanged
    );
    window.addEventListener('scene_asset_changed', handleSceneAssetChanged);

    return () => {
      document.removeEventListener('cancel_export', handleExportCancel);
      window.removeEventListener(
        'refetch_selectable_assets',
        refetchSelectableAssetsState
      );
      window.removeEventListener(
        'stream_editor_notification',
        handleStreamNotification
      );
      window.removeEventListener(
        'add_position_capture_point',
        handleAddPositionCapturePoint
      );
      window.removeEventListener(
        'save_action_position',
        handleSaveActionPosition
      );
      window.removeEventListener(
        'composer_collapsed_changed',
        handleComposerCollapsedChanged
      );
      window.removeEventListener(
        'scene_asset_changed',
        handleSceneAssetChanged
      );
    };
  }, []);

  function listener(e) {
    if (e.keyCode == 90 && e.ctrlKey) {
      undo();
    } else if (e.keyCode == 89 && e.ctrlKey) {
      redo();
    } else if (e.keyCode == 80 && e.ctrlKey && e.shiftKey) {
      e.preventDefault();
      setShowPerf((prev) => !prev);
    }
  }

  function onSaveScene() {
    if (checkEditorState()) {
      return;
    }

    saveScene({
      saveNewTemplate,
      isSignIn,
      router,
      createScene,
      user,
      setSelectedModel,
      setIsTransformControlsActive,
      setIsEditorDrawerOpen,
      updateScene,
      selectedSkybox,
      lightTemplate,
      sceneAssets,
      selectableAssets,
      assetChanged,
      updateAsset,
      createAsset,
      cleanUpDeletedAssets,
      setSceneAssets,
      setSelectableAssets,
      openNotification,
      updateManyAssets,
      defaultSceneTemplate:
        DEFAULT_SCENE_TEMPLATE < 0
          ? null
          : sceneTemplates?.sceneTemplates[DEFAULT_SCENE_TEMPLATE],
      selectedSceneThumbnail,
    });

    setTimeout(() => {
      const newInitialState = JSON.stringify(
        selectableAssets.map((asset) => ({
          id: asset.props?.asset?.id,
          position: asset.props?.asset?.ref?.current?.position,
          rotation: asset.props?.asset?.ref?.current?.rotation,
          scale: asset.props?.asset?.ref?.current?.scale,
        }))
      );
      setInitialAssetsState(newInitialState);
      setHasChanges(false);

      window.dispatchEvent(
        new CustomEvent('scene_asset_changed', {
          detail: { hasChanges: false },
        })
      );
    }, 1000);
  }

  const selectObject = (obj) => {
    if (!obj) return;

    const parent = searchParentRef(obj, 'StudioID');
    let objectSelectAct = obj;
    if (parent) {
      objectSelectAct = parent;
    }

    if (!objectSelectAct || !objectSelectAct.uuid) return;

    const selected = selectableAssets.find(
      (elem) =>
        elem.props.asset.ref.current &&
        elem.props.asset.ref.current.uuid === objectSelectAct.uuid
    );

    if (selected) {
      const asset = selected.props.asset;
      const threeObj = selected.props.asset.ref.current;
      if (!threeObj) return;

      const propsOut = {
        position: threeObj.position,
        scale: threeObj.scale,
        rotation: threeObj.rotation,
        textureToChange: asset.textureToChange,
        textureToChangeNormal: asset.textureToChangeNormal,
        textureToChangeRoughness: asset.textureToChangeRoughness,
        textureToChangeMetalness: asset.textureToChangeMetalness,
      };

      setSelectedModelProps(() => propsOut);
      window.dispatchEvent(
        new CustomEvent('select_asset', {
          detail: { assetId: selected.props.asset.id },
        })
      );
      setSelectedModel(selected);
      setIsTransformControlsActive(true);

      if (
        !isTimelineMode &&
        [
          ASSET_TYPE.MISC.POSITIONAL_AUDIO,
          ASSET_TYPE.PRIMITIVE_TEXTURE.VIDEO_PLANE,
        ].includes(selected.props.asset.type)
      ) {
        setIsEditorDrawerOpen(() => true);
      } else if (
        !isTimelineMode &&
        selected.props.asset.type === ASSET_TYPE.MISC.LIGHT
      ) {
        setIsEditorDrawerOpen(() => false);
        setIsMaterialDrawerOpen(() => false);
      } else if (
        !isTimelineMode &&
        selected.props.asset.type === ASSET_TYPE.GLTF.OTHER &&
        selected.props.asset.isPortal
      ) {
        setIsEditorDrawerOpen(() => true);
        setIsLightDrawerOpen(() => false);
        setIsMaterialDrawerOpen(() => false);
      } else {
        setIsEditorDrawerOpen(() => false);
        setIsLightDrawerOpen(() => false);
        setIsMaterialDrawerOpen(() => false);
      }
    }
  };

  const handleTimelineClick = useCallback(async () => {
    if (hasChanges) {
      window.dispatchEvent(
        new CustomEvent('stream_editor_notification', {
          detail: {
            message: 'Saving scene before switching to Timeline...',
            type: 'info',
          },
        })
      );

      await onSaveScene();
    }

    setIsTimelineMode(true);
    setIsTimelineOpen(true);
    setIsEditorDrawerOpen(false);
    setIsLightDrawerOpen(false);
    setIsMaterialDrawerOpen(false);
    setSelectedModel(null);
    setIsTransformControlsActive(false);
  }, [sceneId, hasChanges, onSaveScene]);

  const handleEditorClick = () => {
    if (typeof saveTimelineRef.current === 'function') {
      saveTimelineRef.current();
    }

    const wasInTimelineMode = isTimelineMode;
    setIsTimelineMode(false);
    setIsTimelineOpen(false);
    if (wasInTimelineMode && isComposerCollapsed) {
      setIsComposerCollapsed(false);
      setSplitterKey((prev) => prev + 1);
    }
  };

  const handleUpdateShaderProperties = (properties: any) => {
    if (selectedModel && selectedModel.props?.asset) {
      console.log('Updating shader properties:', properties);

      const updatedAsset = {
        ...selectedModel.props.asset,
        metadata: {
          ...selectedModel.props.asset.metadata,
          shaderParams: {
            ...properties,
          },
        },
      };

      const updatedModel = {
        ...selectedModel,
        props: {
          ...selectedModel.props,
          asset: updatedAsset,
        },
      };

      const updatedAssets = selectableAssets.map((asset) => {
        if (asset.props.instanceId === selectedModel.props.instanceId) {
          return updatedModel;
        }
        return asset;
      });

      setSelectableAssets({
        type: 'set',
        newState: updatedAssets,
      });

      setSelectedModel(updatedModel);
    }
  };

  const checkForChanges = useCallback(() => {
    const currentAssetsState = JSON.stringify(
      selectableAssets.map((asset) => ({
        id: asset.props?.asset?.id,
        position: asset.props?.asset?.ref?.current?.position,
        rotation: asset.props?.asset?.ref?.current?.rotation,
        scale: asset.props?.asset?.ref?.current?.scale,
      }))
    );

    const hasChanges = currentAssetsState !== initialAssetsState;

    setHasChanges(hasChanges);

    window.dispatchEvent(
      new CustomEvent('editor_has_changes', {
        detail: { hasChanges },
      })
    );
  }, [selectableAssets, initialAssetsState]);

  const initialAssetsTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (initialAssetsTimerRef.current) {
      clearTimeout(initialAssetsTimerRef.current);
      initialAssetsTimerRef.current = null;
    }

    if (selectableAssets.length > 0 && !initialAssetsState) {
      initialAssetsTimerRef.current = setTimeout(() => {
        const allAssetsReady = selectableAssets.every(
          (asset) => asset.props?.asset?.ref?.current
        );

        if (allAssetsReady) {
          const initialState = JSON.stringify(
            selectableAssets.map((asset) => ({
              id: asset.props?.asset?.id,
              position: asset.props?.asset?.ref?.current?.position,
              rotation: asset.props?.asset?.ref?.current?.rotation,
              scale: asset.props?.asset?.ref?.current?.scale,
            }))
          );
          setInitialAssetsState(initialState);
        }
        initialAssetsTimerRef.current = null;
      }, 1000);
    }

    if (initialAssetsState) {
      checkForChanges();
    }

    return () => {
      if (initialAssetsTimerRef.current) {
        clearTimeout(initialAssetsTimerRef.current);
        initialAssetsTimerRef.current = null;
      }
    };
  }, [selectableAssets, initialAssetsState, checkForChanges]);

  return (
    <>
      {checkEditorState() ? (
        <div>loading...</div>
      ) : (
        <>
          <Splitter
            layout='vertical'
            className={styles.mainSplitter}
            key={splitterKey}
            onResize={(sizes) => {
              const composerSize = sizes[1];
              setIsComposerCollapsed(composerSize === 0);
            }}
          >
            <Splitter.Panel className={styles.splitterCanvas}>
              <EditorMenu
                user={user}
                isAddedInitialPoint={isAddedInitialPoint}
                setIsAddedInitialPoint={setIsAddedInitialPoint}
                setSelectableAssets={setSelectableAssets}
                selectedModel={selectedModel}
                deleteSelectedModel={deleteSelectedModel}
                assetsToDelete={assetsToDelete}
                setAssetsToDelete={setAssetsToDelete}
                setSelectedModel={setSelectedModel}
                setIsTransformControlsActive={setIsTransformControlsActive}
                sceneTemplates={sceneTemplates}
                buildScene={buildScene}
                skyBoxIn={skyBoxIn}
                lightsIn={lightsIn}
                camera={camera}
                cameraInitialPosition={cameraInitialPosition}
                selectableAssets={selectableAssets}
                loadSceneTemplate={loadSceneTemplate}
                saveScene={onSaveScene}
                setCurrentCameraConfig={setCurrentCameraConfig}
                setSelectedSkybox={setSelectedSkybox}
                setLightTemplate={setLightTemplate}
                isTransformControlsActive={isTransformControlsActive}
                tourRefs={tourRefs}
                setIsTourOpen={setIsTourOpen}
                isEditorDrawerOpen={isEditorDrawerOpen}
                currentCameraConfig={currentCameraConfig}
                selectObject={selectObject}
                lightTemplate={lightTemplate}
                selectedSkybox={selectedSkybox}
                selectedSceneTemplate={selectedSceneTemplate}
                setIsEnableShadowLight={setIsEnableShadowLight}
                saveCameraConfig={saveCameraConfig}
                isOpenWorlds={isOpenWorlds}
                setIsOpenWorlds={setIsOpenWorlds}
                sceneId={sceneId}
                setIsPortalModalOpen={setIsPortalModalOpen}
                isLinkModalOpen={isLinkModalOpen}
                setIsLinkModalOpen={setIsLinkModalOpen}
                isPOIModalOpen={isPOIModalOpen}
                setIsPOIModalOpen={setIsPOIModalOpen}
                isPortalModalOpen={isPortalModalOpen}
                scene={scene}
              />
              <div className={styles.mainContentWrapper}>
                <div
                  className={styles.nodeManager}
                  style={{ zIndex: isTimelineMode ? 4 : 0 }}
                >
                  <TimelineNodeManager
                    isOpen={true}
                    setIsOpen={setIsTimelineOpen}
                    selectableAssets={selectableAssets}
                    userSceneId={sceneId as string}
                    isFullscreen={true}
                    onSetNodesReady={handleSetTimelineSetNodes}
                    saveTimelineRef={saveTimelineRef}
                  />
                </div>
                <div
                  className={styles.canvas}
                  style={{ zIndex: !isTimelineMode ? 4 : 0 }}
                >
                  <Canvas
                    dpr={DPR}
                    shadows
                    tabIndex={1}
                    onKeyDown={listener}
                    onCreated={(data) => {
                      setScene(data.scene);
                    }}
                  >
                    <FirstPersonDragControls ref={cameraControlsRef} />
                    <Renderer />
                    <EventManager
                      setEditorMode={setEditorMode}
                      onDeleteModel={deleteSelectedModel}
                      selectedModel={selectedModel}
                      selectableAssets={selectableAssets}
                      setSelectedModel={setSelectedModel}
                    />
                    <PerspectiveCamera
                      makeDefault
                      fov={currentCameraConfig.fov}
                      near={currentCameraConfig.near}
                      far={currentCameraConfig.far}
                      position={cameraInitialPosition}
                      ref={setCamera}
                    />
                    <gridHelper args={[100, 100, 0x07bb4f, 0x888888]} />
                    <lineSegments rotation={[-Math.PI / 2, 0, 0]}>
                      <edgesGeometry
                        attach='geometry'
                        args={[new THREE.PlaneGeometry(100, 100)]}
                      />
                      <lineBasicMaterial
                        attach='material'
                        color={0x07bb4f}
                        linewidth={3}
                      />
                    </lineSegments>
                    {showPerf && (
                      <Perf
                        position='top-right'
                        style={{ marginTop: '64px' }}
                      />
                    )}
                    {selectedSkybox && <Skybox item={selectedSkybox} />}
                    {isTransformControlsActive && selectedModel && (
                      <SceneEditor
                        selectedModel={selectedModel}
                        editorMode={editorMode}
                        selectedModelProps={selectedModelProps}
                        setSelectedModelProps={setSelectedModelProps}
                        dispatchChangeAsset={setSelectableAssets}
                      />
                    )}
                    <Select
                      onClick={(e) => {
                        if ((window as any).justFinishedDragging) {
                          (window as any).justFinishedDragging = false;
                          return;
                        }

                        if (e.intersections.length > 0) {
                          let closerObj = e.intersections[0];
                          e.intersections.forEach((obj) => {
                            if (obj.distance < closerObj.distance)
                              closerObj = obj;
                          });

                          selectObject(closerObj.object);
                        }
                      }}
                    >
                      {selectableAssets.map((selectableAsset) => (
                        <Suspense
                          key={`Suspense-${selectableAsset.props.instanceId}`}
                          fallback={null}
                        >
                          <ModelErrorHandler
                            key={`Model-Error-${selectableAsset.props.instanceId}`}
                            {...selectableAsset.props}
                          >
                            {selectableAsset}
                          </ModelErrorHandler>
                        </Suspense>
                      ))}
                    </Select>
                    <Suspense fallback={null}>
                      {sceneAssets.map((sceneAsset) => {
                        return (
                          <ModelErrorHandler
                            key={`Model-Error-${sceneAsset.props.instanceId}`}
                            {...sceneAsset.props}
                          >
                            {sceneAsset}
                          </ModelErrorHandler>
                        );
                      })}
                      {postprocessingData?.findFirstPostProcessingSettings && (
                        <PostprocessingSettings
                          postprocessingSettings={
                            postprocessingData.findFirstPostProcessingSettings
                          }
                        />
                      )}
                    </Suspense>
                  </Canvas>
                </div>
              </div>
            </Splitter.Panel>
            <Splitter.Panel
              collapsible
              className={styles.splitterComposer}
              defaultSize={250}
              min={0}
              max={250}
            >
              <EditorSwitcher
                isTimelineMode={isTimelineMode}
                onTimelineClick={handleTimelineClick}
                onEditorClick={handleEditorClick}
              />
              <EditorComposer
                assets={selectableAssets}
                camera={camera}
                sceneId={sceneId!}
                selectObject={selectObject}
                selectedModel={selectedModel}
                cameraControls={cameraControlsRef.current}
                deleteSelectedModel={deleteSelectedModel}
                undo={undo}
                redo={redo}
                isTransformControlsActive={isTransformControlsActive}
                editorMode={editorMode}
                setEditorMode={setEditorMode}
                setIsTransformControlsActive={setIsTransformControlsActive}
                setIsEditorDrawerOpen={setIsEditorDrawerOpen}
                setIsMaterialDrawerOpen={setIsMaterialDrawerOpen}
                setSelectedModel={setSelectedModel}
                setSelectableAssets={setSelectableAssets}
                setIsLinkModalOpen={setIsLinkModalOpen}
                setIsPOIModalOpen={setIsPOIModalOpen}
                setIsPortalModalOpen={setIsPortalModalOpen}
                isTimelineMode={isTimelineMode}
                setIsLightDrawerOpen={setIsLightDrawerOpen}
                setIsShaderDrawerOpen={setIsShaderDrawerOpen}
              />
            </Splitter.Panel>
          </Splitter>
          {!isTimelineMode && (
            <EditorDrawer
              isOpen={isEditorDrawerOpen}
              setIsOpen={setIsEditorDrawerOpen}
              selectedModel={selectedModel}
              selectedModelProps={selectedModelProps}
              dispatchChangeModels={setSelectableAssets}
              setSelectedModelProps={setSelectedModelProps}
            />
          )}

          <LightSettingsDrawer
            selectedModel={selectedModel}
            isOpen={isLightDrawerOpen}
            onClose={() => setIsLightDrawerOpen(false)}
          />

          <MaterialEditorDrawer
            selectedModel={selectedModel}
            selectedModelProps={selectedModelProps}
            dispatchChangeModels={setSelectableAssets}
            setSelectedModelProps={setSelectedModelProps}
            isOpen={isMaterialDrawerOpen}
            onClose={() => setIsMaterialDrawerOpen(false)}
          />

          <ShaderPropertiesDrawer
            selectedModel={selectedModel}
            isOpen={isShaderDrawerOpen}
            onClose={() => setIsShaderDrawerOpen(false)}
            onUpdateShaderProperties={handleUpdateShaderProperties}
          />
        </>
      )}
    </>
  );
};

export default Editor;
